import { useState, useCallback, useEffect } from "react";
import { api, fmtMoney } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { toast } from "sonner";
import { Calculator, CheckCircle2, XCircle } from "lucide-react";

// Two states only (feasible / not feasible) — not the three-state
// BAND_STYLE pattern ReadinessTab uses for ready/developing/not_ready.
const FEASIBLE_STYLE = {
    true:  { text: "text-emerald-700", bg: "bg-emerald-50", border: "border-emerald-200", dot: "bg-emerald-500" },
    false: { text: "text-red-700",     bg: "bg-red-50",     border: "border-red-200",     dot: "bg-red-500"     },
};

function splitLabel(cashPct) {
    const cash = Math.round(cashPct * 100);
    const deferred = 100 - cash;
    return deferred === 0 ? `${cash}% cash` : `${cash}% cash / ${deferred}% deferred`;
}

export function FeasibilityTab({ dealId, deal }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [calculating, setCalculating] = useState(false);
    const [cashInput, setCashInput] = useState("");
    const [error, setError] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await api.get(`/deals/${dealId}/feasibility`);
            setData(res.data);
        } catch (e) {
            if (e.response?.status === 404) {
                // No check run yet — this is the normal empty state, not an error.
                setData(null);
            } else {
                setError(e.response?.data?.detail || "Could not load feasibility check.");
            }
        } finally {
            setLoading(false);
        }
    }, [dealId]);

    useEffect(() => { load(); }, [load]);

    const handleCalculate = async () => {
        const buyer_available_cash = parseFloat(cashInput);
        if (Number.isNaN(buyer_available_cash) || buyer_available_cash < 0) {
            toast.error("Enter a valid available cash amount");
            return;
        }
        setCalculating(true);
        try {
            const res = await api.post(`/deals/${dealId}/feasibility`, { buyer_available_cash });
            setData(res.data);
            toast.success("Feasibility check calculated");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not calculate feasibility check.");
        } finally {
            setCalculating(false);
        }
    };

    return (
        <div className="space-y-6" data-testid="feasibility-tab">
            <div>
                <h2 className="font-display text-xl font-bold tracking-tight">Deal Feasibility Check</h2>
                <p className="text-xs text-muted-foreground mt-0.5">
                    Cash / deferred consideration sensitivity, for mandate prioritisation.
                </p>
            </div>

            {/* Deal context — asking price & normalised EBITDA, read-only */}
            <div className="grid grid-cols-2 gap-4 max-w-md">
                <div className="border border-border rounded-md p-4 bg-white">
                    <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Asking Price</div>
                    <div className="font-mono text-sm">
                        {deal?.asking_price ? fmtMoney(deal.asking_price, deal.currency) : "—"}
                    </div>
                </div>
                <div className="border border-border rounded-md p-4 bg-white">
                    <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Normalised EBITDA</div>
                    <div className="font-mono text-sm">
                        {deal?.normalised_ebitda ? fmtMoney(deal.normalised_ebitda, deal.currency) : "—"}
                    </div>
                </div>
            </div>

            {/* Input form */}
            <div className="border border-border rounded-md p-4 bg-white max-w-md">
                <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Buyer Available Cash</div>
                <div className="flex items-center gap-2">
                    <Input
                        type="number"
                        min="0"
                        step="1000"
                        placeholder="e.g. 750000"
                        value={cashInput}
                        onChange={(e) => setCashInput(e.target.value)}
                        data-testid="feasibility-input"
                        className="font-mono"
                    />
                    <Button
                        onClick={handleCalculate}
                        disabled={calculating || !cashInput}
                        data-testid="feasibility-calculate-btn"
                        className="gap-1.5 flex-shrink-0"
                    >
                        <Calculator className="w-3.5 h-3.5" />
                        {calculating ? "Calculating…" : "Calculate"}
                    </Button>
                </div>
            </div>

            {loading ? (
                <div className="py-12 text-center text-sm text-muted-foreground" data-testid="feasibility-loading">
                    Loading…
                </div>
            ) : error ? (
                <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-4" data-testid="feasibility-error">
                    {error}
                </div>
            ) : data ? (
                <div className="space-y-3" data-testid="feasibility-splits">
                    {(data.outputs?.sensitivity_splits || []).map((split) => {
                        const c = FEASIBLE_STYLE[String(split.feasible)];
                        return (
                            <div
                                key={split.cash_pct}
                                className={`border rounded-md p-4 flex items-center justify-between ${c.border} ${c.bg}`}
                                data-testid={`feasibility-split-${split.cash_pct}`}
                            >
                                <div>
                                    <div className={`text-sm font-medium ${c.text}`}>{splitLabel(split.cash_pct)}</div>
                                    <div className="text-xs text-muted-foreground mt-1 font-mono">
                                        Cash {fmtMoney(split.cash_amount, deal?.currency)} · Deferred {fmtMoney(split.deferred_amount, deal?.currency)}
                                    </div>
                                </div>
                                <div className={`flex items-center gap-1.5 text-xs uppercase tracking-wider font-semibold ${c.text}`}>
                                    {split.feasible ? (
                                        <>
                                            <CheckCircle2 className={`w-4 h-4 ${c.dot ? "" : ""}`} />
                                            Feasible
                                        </>
                                    ) : (
                                        <>
                                            <XCircle className="w-4 h-4" />
                                            Not feasible
                                        </>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            ) : (
                <div className="text-sm text-muted-foreground bg-muted/40 rounded-md p-6 text-center" data-testid="feasibility-empty">
                    No feasibility check run yet. Enter the buyer's available cash above and calculate.
                </div>
            )}
        </div>
    );
}
